import React, { useEffect, useState } from "react";
import Button from "../Shared/Button";
import {
  daysToYearsLabel,
  getSecureFileRetentionSettings,
  RETENTION_YEAR_PRESETS,
  updateSecureFileRetentionSettings,
} from "../../services/secureFileRetention";

const SecureFileRetentionSettings = () => {
  const [patientFileRetentionDays, setPatientFileRetentionDays] = useState(7 * 365);
  const [globalFileRetentionDays, setGlobalFileRetentionDays] = useState(7 * 365);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedByName, setUpdatedByName] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    const loadSettings = async () => {
      setIsLoading(true);
      setError("");
      const { data, error: loadError } = await getSecureFileRetentionSettings();
      if (loadError) {
        setError(loadError.message);
      } else if (data) {
        setPatientFileRetentionDays(data.patientFileRetentionDays);
        setGlobalFileRetentionDays(data.globalFileRetentionDays);
        setUpdatedAt(data.updatedAt);
        setUpdatedByName(data.updatedByName || "");
      }
      setIsLoading(false);
    };

    loadSettings();
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    setError("");
    setSuccessMessage("");

    const { data, error: saveError } = await updateSecureFileRetentionSettings({
      patientFileRetentionDays,
      globalFileRetentionDays,
    });

    if (saveError) {
      setError(saveError.message);
    } else {
      setPatientFileRetentionDays(data.patientFileRetentionDays);
      setGlobalFileRetentionDays(data.globalFileRetentionDays);
      setUpdatedAt(data.updatedAt);
      setUpdatedByName(data.updatedByName || "");
      setSuccessMessage("Retention settings saved.");
    }

    setIsSaving(false);
  };

  const renderPresetButtons = (value, onChange) => (
    <div className="buttons are-small mb-3">
      {RETENTION_YEAR_PRESETS.map((preset) => (
        <button
          key={preset.value}
          type="button"
          className={`button is-radiusless ${value === preset.value ? "is-info" : "is-light"}`}
          onClick={() => onChange(preset.value)}
        >
          {preset.label}
        </button>
      ))}
    </div>
  );

  if (isLoading) {
    return <p className="is-size-7 has-text-grey">Loading secure file retention settings...</p>;
  }

  return (
    <div className="box is-shadowless">
      <h2 className="title is-5">Secure File Retention</h2>
      <p className="is-size-7 has-text-grey mb-4">
        Uploaded secure files are permanently deleted after the retention period, measured from the upload date. The
        daily scheduler removes expired files from storage, Firestore, and related audit logs. Share link expiry (30
        days) is separate from this retention policy.
      </p>

      {error && (
        <div className="notification is-danger is-light is-size-7">
          <button className="delete" onClick={() => setError("")} aria-label="dismiss error" />
          {error}
        </div>
      )}

      {successMessage && (
        <div className="notification is-success is-light is-size-7">
          <button className="delete" onClick={() => setSuccessMessage("")} aria-label="dismiss success" />
          {successMessage}
        </div>
      )}

      <div className="columns">
        <div className="column is-half">
          <p className="menu-label">Patient secure files</p>
          {renderPresetButtons(patientFileRetentionDays, setPatientFileRetentionDays)}
          <div className="field">
            <label className="label is-size-7" htmlFor="patient-retention-days">
              Retention period (days)
            </label>
            <div className="control">
              <input
                id="patient-retention-days"
                className="input is-small is-radiusless"
                type="number"
                min={30}
                max={3650}
                value={patientFileRetentionDays}
                onChange={(event) => setPatientFileRetentionDays(Number(event.target.value))}
              />
            </div>
            <p className="help is-size-7">Currently {daysToYearsLabel(patientFileRetentionDays)}. Minimum 30 days.</p>
          </div>
        </div>

        <div className="column is-half">
          <p className="menu-label">Global secure files</p>
          {renderPresetButtons(globalFileRetentionDays, setGlobalFileRetentionDays)}
          <div className="field">
            <label className="label is-size-7" htmlFor="global-retention-days">
              Retention period (days)
            </label>
            <div className="control">
              <input
                id="global-retention-days"
                className="input is-small is-radiusless"
                type="number"
                min={30}
                max={3650}
                value={globalFileRetentionDays}
                onChange={(event) => setGlobalFileRetentionDays(Number(event.target.value))}
              />
            </div>
            <p className="help is-size-7">Currently {daysToYearsLabel(globalFileRetentionDays)}. Minimum 30 days.</p>
          </div>
        </div>
      </div>

      {updatedAt && (
        <p className="is-size-7 has-text-grey mb-4">
          Last updated {new Date(updatedAt).toLocaleString()}
          {updatedByName ? ` by ${updatedByName}` : ""}.
        </p>
      )}

      <Button text="SAVE RETENTION SETTINGS" onClick={handleSave} isLoading={isSaving} />
    </div>
  );
};

export default SecureFileRetentionSettings;
